import { API_BASE_URL, resolvePublicMediaUrl } from '../config/api';
import {
    normalizeMemberSearchText,
    sortClubMembersByGivenName,
} from '../data/members';

/**
 * @param {string | null | undefined} fullName
 * @returns {{ givenName: string, familyName: string }}
 */
export function splitMemberFullName(fullName) {
    const t = (fullName || '').trim();
    if (!t) return { givenName: '', familyName: '' };
    const parts = t.split(/\s+/);
    if (parts.length === 1) {
        return { givenName: parts[0], familyName: '' };
    }
    const familyName = /** @type {string} */ (parts.pop());
    const givenName = parts.join(' ');
    return { givenName, familyName };
}

/**
 * @param {string | null | undefined} iso
 */
function toYmd(iso) {
    if (!iso || typeof iso !== 'string') return '';
    return iso.length >= 10 ? iso.slice(0, 10) : '';
}

/**
 * @param {Record<string, unknown>} api
 */
function formatProfession(api) {
    const job =
        api.jobTitle != null && String(api.jobTitle).trim()
            ? String(api.jobTitle).trim()
            : '';
    const co =
        api.company != null && String(api.company).trim()
            ? String(api.company).trim()
            : '';
    if (job && co) return `${job} · ${co}`;
    return job || co || '—';
}

/**
 * @param {Record<string, unknown>} api
 * @returns {import('../data/members').ClubMember}
 */
export function mapApiMemberToClubMember(api) {
    const { givenName, familyName } = splitMemberFullName(
        /** @type {string} */ (api.fullName),
    );
    const subs = api.subMembers;
    const sub =
        Array.isArray(subs) && subs[0] && typeof subs[0] === 'object'
            ? /** @type {Record<string, unknown>} */ (subs[0])
            : null;
    let partnerGivenName;
    let partnerFamilyName;
    /** @type {string} */
    let partnerProfilePictureUrl = '';
    if (sub?.fullName) {
        const p = splitMemberFullName(String(sub.fullName));
        partnerGivenName = p.givenName;
        partnerFamilyName = p.familyName;
    }
    if (sub?.profilePictureUrl) {
        partnerProfilePictureUrl = resolvePublicMediaUrl(
            String(sub.profilePictureUrl),
        );
    }
    const partnerId =
        sub?.id != null && String(sub.id).trim()
            ? String(sub.id).trim()
            : undefined;
    const company =
        api.company != null && String(api.company).trim()
            ? String(api.company).trim()
            : '';
    const sector =
        api.sector != null && String(api.sector).trim()
            ? String(api.sector).trim()
            : '';

    const profilePictureUrl = api.profilePictureUrl
        ? resolvePublicMediaUrl(String(api.profilePictureUrl))
        : '';

    return {
        id: String(api.id ?? ''),
        givenName,
        familyName,
        partnerGivenName,
        partnerFamilyName,
        partnerId,
        partnerProfilePictureUrl: partnerProfilePictureUrl || undefined,
        birthDate: toYmd(/** @type {string | undefined} */ (api.birthDate)),
        joinedAt: toYmd(/** @type {string | undefined} */ (api.clubJoinDate)),
        profession: formatProfession(api),
        email:
            api.email != null && String(api.email).trim()
                ? String(api.email).trim()
                : '',
        company,
        sector,
        profilePictureUrl: profilePictureUrl || undefined,
    };
}

/**
 * @param {Response} res
 * @param {string} readErrorLabel
 * @param {string} failLabel
 */
async function parseMemberListJsonResponse(res, readErrorLabel, failLabel) {
    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(readErrorLabel);
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `${failLabel} (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : failLabel;
        throw new Error(msg);
    }

    const raw = /** @type {{ data?: unknown, metaData?: unknown }} */ (obj)
        .data;
    const list = Array.isArray(raw) ? raw : [];

    return list;
}

/**
 * @param {string} accessToken
 * @param {number} pageNumber
 * @param {number} pageSize
 * @param {AbortSignal} [signal]
 */
async function fetchMembersPage(accessToken, pageNumber, pageSize, signal) {
    const params = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
    });
    const url = `${API_BASE_URL}/members/api/Member?${params}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        signal,
    });

    const list = await parseMemberListJsonResponse(
        res,
        'Üye listesi okunamadı',
        'Üye listesi alınamadı',
    );
    return { list };
}

const SEARCH_PAGE_SIZE = 500;

/**
 * `GET .../Member/search?keyword=&pageNumber=1&pageSize=`
 * @param {string} accessToken
 * @param {string} keyword
 * @param {{ signal?: AbortSignal, pageSize?: number }} [opts]
 * @returns {Promise<import('../data/members').ClubMember[]>}
 */
export async function fetchMembersSearch(accessToken, keyword, opts = {}) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const k = normalizeMemberSearchText(keyword);
    if (!k) {
        return [];
    }

    const pageSize = opts.pageSize ?? SEARCH_PAGE_SIZE;
    const params = new URLSearchParams({
        keyword: k,
        pageNumber: '1',
        pageSize: String(pageSize),
    });
    const url = `${API_BASE_URL}/members/api/Member/search?${params}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        signal: opts.signal,
    });

    const list = await parseMemberListJsonResponse(
        res,
        'Arama sonucu okunamadı',
        'Arama yapılamadı',
    );

    /** @type {import('../data/members').ClubMember[]} */
    const out = [];
    for (const row of list) {
        if (row && typeof row === 'object') {
            const m = mapApiMemberToClubMember(
                /** @type {Record<string, unknown>} */ (row),
            );
            if (m.id) out.push(m);
        }
    }

    return sortClubMembersByGivenName(out);
}

/** Tek istekte alınacak üst sınır (sunucu pageSize kabul ediyorsa). */
const FETCH_PAGE_SIZE = 500;

/**
 * Üye rehberini tek istekle çeker (`pageNumber=1`, büyük `pageSize`).
 * @param {string} accessToken
 * @returns {Promise<import('../data/members').ClubMember[]>}
 */
export async function fetchAllMembersDirectory(accessToken) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }

    const { list } = await fetchMembersPage(accessToken, 1, FETCH_PAGE_SIZE);

    /** @type {import('../data/members').ClubMember[]} */
    const out = [];
    for (const row of list) {
        if (row && typeof row === 'object') {
            const m = mapApiMemberToClubMember(
                /** @type {Record<string, unknown>} */ (row),
            );
            if (m.id) out.push(m);
        }
    }

    return sortClubMembersByGivenName(out);
}

/**
 * @param {Response} res
 */
async function parseMemberDetailJsonResponse(res) {
    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Üye bilgisi okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Üye bilgisi alınamadı (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Üye bulunamadı';
        throw new Error(msg);
    }

    const data = /** @type {{ data?: unknown }} */ (obj).data;
    if (!data || typeof data !== 'object') {
        throw new Error('Üye bulunamadı');
    }

    return /** @type {Record<string, unknown>} */ (data);
}

/**
 * URL id ile karşılaştırma (UUID tire / büyük-küçük farkı yok sayılır).
 * @param {string | null | undefined} id
 */
function normalizeMemberId(id) {
    if (id == null) return '';
    return String(id).trim().toLowerCase().replace(/-/g, '');
}

/**
 * Path'teki `:memberId` ile API `data.id` veya `data.subMembers[].id` eşleşmesine göre
 * gösterilecek ham kaydı seçer (aynı GET yanıtında partner kök veya alt kayıt).
 * @param {Record<string, unknown>} api
 * @param {string} memberIdFromPath
 */
export function pickDisplayRecordForPath(api, memberIdFromPath) {
    const want = normalizeMemberId(memberIdFromPath);
    const rootId = normalizeMemberId(api.id);
    if (want && rootId && want === rootId) {
        return api;
    }
    const subs = Array.isArray(api.subMembers) ? api.subMembers : [];
    for (const s of subs) {
        if (s && typeof s === 'object') {
            const rec = /** @type {Record<string, unknown>} */ (s);
            const sid = normalizeMemberId(rec.id);
            if (want && sid && want === sid) {
                return rec;
            }
        }
    }
    return api;
}

/**
 * Partner profili: ana üyeye bağlı `referenceMemberId`.
 * @param {Record<string, unknown>} api
 */
function isPartnerProfileRecord(api) {
    const ref = api.referenceMemberId;
    return ref != null && String(ref).trim() !== '';
}

/**
 * @param {unknown} row
 */
export function mapApiChildToView(row) {
    if (!row || typeof row !== 'object') {
        return {
            id: '',
            fullName: '',
            firstName: '',
            lastName: '',
            birthDate: null,
        };
    }
    const r = /** @type {Record<string, unknown>} */ (row);
    const first = String(r.firstName ?? '').trim();
    const last = String(r.lastName ?? '').trim();
    const fullName =
        [first, last].filter(Boolean).join(' ') ||
        String(r.fullName ?? '').trim() ||
        String(r.name ?? '').trim();
    const birthRaw = r.birthDate;
    const birthDate =
        birthRaw != null && String(birthRaw).trim()
            ? String(birthRaw).trim()
            : null;
    return {
        id: r.id != null ? String(r.id) : '',
        fullName,
        firstName: first,
        lastName: last,
        birthDate,
    };
}

/**
 * @param {unknown} row
 */
/**
 * @param {Record<string, unknown>} r
 */
function readOptionalStr(r, key) {
    const v = r[key];
    return v != null && String(v).trim() ? String(v).trim() : '';
}

export function mapApiSpouseToView(row) {
    if (!row || typeof row !== 'object') {
        return {
            id: '',
            fullName: '',
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            jobTitle: '',
            company: '',
            sector: '',
            university: '',
            highSchool: '',
            address: '',
            workAddress: '',
            tcKimlikNo: '',
            birthDate: null,
            clubJoinDate: null,
            website: '',
            hobbies: '',
            facebook: '',
            twitter: '',
            linkedIn: '',
            profilePictureUrl: '',
        };
    }
    const r = /** @type {Record<string, unknown>} */ (row);
    const first = String(r.firstName ?? '').trim();
    const last = String(r.lastName ?? '').trim();
    const fullName =
        [first, last].filter(Boolean).join(' ') ||
        String(r.fullName ?? '').trim();
    return {
        id: r.id != null ? String(r.id) : '',
        fullName,
        firstName: first,
        lastName: last,
        email: readOptionalStr(r, 'email'),
        phoneNumber: readOptionalStr(r, 'phoneNumber'),
        jobTitle: readOptionalStr(r, 'jobTitle'),
        company: readOptionalStr(r, 'company'),
        sector: readOptionalStr(r, 'sector'),
        university: readOptionalStr(r, 'university'),
        highSchool: readOptionalStr(r, 'highSchool'),
        address: readOptionalStr(r, 'address'),
        workAddress: readOptionalStr(r, 'workAddress'),
        tcKimlikNo: readOptionalStr(r, 'tcKimlikNo'),
        birthDate:
            r.birthDate != null && String(r.birthDate).trim()
                ? String(r.birthDate).trim()
                : null,
        clubJoinDate:
            r.clubJoinDate != null && String(r.clubJoinDate).trim()
                ? String(r.clubJoinDate).trim()
                : null,
        website: readOptionalStr(r, 'website'),
        hobbies: readOptionalStr(r, 'hobbies'),
        facebook: readOptionalStr(r, 'facebook'),
        twitter: readOptionalStr(r, 'twitter'),
        linkedIn: readOptionalStr(r, 'linkedIn'),
        profilePictureUrl: r.profilePictureUrl
            ? resolvePublicMediaUrl(String(r.profilePictureUrl))
            : '',
    };
}

/**
 * Tek üye nesnesini eşler; iç içe `subMembers` / `children` boş (alt kayıt için).
 * @param {Record<string, unknown>} api
 */
export function mapApiMemberSubRecordToView(api) {
    return mapApiMemberDetailToView({
        ...api,
        subMembers: [],
        children: [],
    });
}

/**
 * API `GET .../Member/{id}` yanıtındaki `data` nesnesini görünüm modeline çevirir.
 * @param {Record<string, unknown>} api
 */
export function mapApiMemberDetailToView(api) {
    const first = String(api.firstName ?? '').trim();
    const last = String(api.lastName ?? '').trim();
    const fullName =
        [first, last].filter(Boolean).join(' ') ||
        String(api.fullName ?? '').trim();

    return {
        id: String(api.id ?? ''),
        fullName,
        firstName: first,
        lastName: last,
        email:
            api.email != null && String(api.email).trim()
                ? String(api.email).trim()
                : '',
        phoneNumber:
            api.phoneNumber != null && String(api.phoneNumber).trim()
                ? String(api.phoneNumber).trim()
                : '',
        jobTitle:
            api.jobTitle != null && String(api.jobTitle).trim()
                ? String(api.jobTitle).trim()
                : '',
        company:
            api.company != null && String(api.company).trim()
                ? String(api.company).trim()
                : '',
        sector:
            api.sector != null && String(api.sector).trim()
                ? String(api.sector).trim()
                : '',
        university:
            api.university != null && String(api.university).trim()
                ? String(api.university).trim()
                : null,
        highSchool:
            api.highSchool != null && String(api.highSchool).trim()
                ? String(api.highSchool).trim()
                : null,
        address:
            api.address != null && String(api.address).trim()
                ? String(api.address).trim()
                : '',
        workAddress:
            api.workAddress != null && String(api.workAddress).trim()
                ? String(api.workAddress).trim()
                : '',
        tcKimlikNo: readOptionalStr(api, 'tcKimlikNo'),
        profilePictureUrl: api.profilePictureUrl
            ? resolvePublicMediaUrl(String(api.profilePictureUrl))
            : '',
        birthDate:
            api.birthDate != null && String(api.birthDate).trim()
                ? String(api.birthDate)
                : null,
        clubJoinDate:
            api.clubJoinDate != null && String(api.clubJoinDate).trim()
                ? String(api.clubJoinDate)
                : null,
        website:
            api.website != null && String(api.website).trim()
                ? String(api.website).trim()
                : null,
        hobbies:
            api.hobbies != null && String(api.hobbies).trim()
                ? String(api.hobbies).trim()
                : null,
        facebook:
            api.facebook != null && String(api.facebook).trim()
                ? String(api.facebook).trim()
                : null,
        twitter:
            api.twitter != null && String(api.twitter).trim()
                ? String(api.twitter).trim()
                : null,
        linkedIn:
            api.linkedIn != null && String(api.linkedIn).trim()
                ? String(api.linkedIn).trim()
                : null,
        membershipType:
            typeof api.membershipType === 'number' ? api.membershipType : null,
        referenceMemberId:
            api.referenceMemberId != null && String(api.referenceMemberId).trim()
                ? String(api.referenceMemberId).trim()
                : null,
        isPartnerProfile: isPartnerProfileRecord(api),
        spouses: [],
        children: [],
        groupLeader: false,
        partnerGivenName: undefined,
        partnerFamilyName: undefined,
        partnerProfilePictureUrl: undefined,
    };
}

/**
 * `GET ${API_BASE_URL}/members/api/Member/{id}`
 * @param {string} accessToken
 * @param {string} memberId
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<ReturnType<typeof mapApiMemberDetailToView>>}
 */
export async function fetchMemberById(accessToken, memberId, opts = {}) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const rawId = String(memberId || '').trim();
    if (!rawId) {
        throw new Error('Üye kimliği gerekli');
    }
    const url = `${API_BASE_URL}/members/api/Member/${encodeURIComponent(rawId)}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        signal: opts.signal,
    });

    const data = await parseMemberDetailJsonResponse(res);
    const picked = pickDisplayRecordForPath(data, memberId);
    const view = mapApiMemberDetailToView(picked);

    const rootChildren = Array.isArray(data.children) ? data.children : [];
    view.children = rootChildren.map((row) => mapApiChildToView(row));

    const want = normalizeMemberId(memberId);
    const viewingRoot = want === normalizeMemberId(data.id);
    const rootSubs = Array.isArray(data.subMembers) ? data.subMembers : [];

    if (viewingRoot) {
        view.spouses = rootSubs
            .filter((s) => s && typeof s === 'object')
            .map((row) => mapApiSpouseToView(row));
    } else {
        const viewingSubMember = rootSubs.some(
            (s) =>
                s &&
                typeof s === 'object' &&
                normalizeMemberId(
                    /** @type {Record<string, unknown>} */ (s).id,
                ) === want,
        );
        if (viewingSubMember) {
            view.isPartnerProfile = true;
            view.spouses = [mapApiSpouseToView(data)];
        }
    }

    return view;
}

/**
 * Oturum açmış üyenin profilini döner.
 * `GET ${API_BASE_URL}/members/api/Member/me`
 * @param {string} accessToken
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<ReturnType<typeof mapApiMemberDetailToView>>}
 */
export async function fetchCurrentMember(accessToken, opts = {}) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const url = `${API_BASE_URL}/members/api/Member/me`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        signal: opts.signal,
    });

    const data = await parseMemberDetailJsonResponse(res);
    return buildMemberDetailViewFromApiData(data);
}

/**
 * API üye kök `data` nesnesinden tam profil görünüm modeli üretir.
 * @param {Record<string, unknown>} data
 */
export function buildMemberDetailViewFromApiData(data) {
    const view = mapApiMemberDetailToView(data);

    const rootChildren = Array.isArray(data.children) ? data.children : [];
    view.children = rootChildren.map((row) => mapApiChildToView(row));

    const rootSubs = Array.isArray(data.subMembers) ? data.subMembers : [];
    view.spouses = rootSubs
        .filter((s) => s && typeof s === 'object')
        .map((row) => mapApiSpouseToView(row));

    return view;
}

/**
 * Yüklenen profil fotoğrafı yolunu API ile uyumlu göreli yola çevirir.
 * @param {unknown} data
 */
export function normalizeUploadedProfilePicturePath(data) {
    const t = String(data ?? '').trim();
    if (!t) return '';
    if (t.startsWith('/api/')) return t;
    if (t.startsWith('/members/')) return `/api/members${t}`;
    return t.startsWith('/') ? t : `/${t}`;
}

/**
 * Oturum açmış üyenin profil fotoğrafını günceller.
 * `POST ${API_BASE_URL}/members/api/Member/me/profile-picture`
 * @param {string} accessToken
 * @param {File} file
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<{ message: string; profilePictureUrl: string }>}
 */
export async function uploadCurrentMemberProfilePicture(
    accessToken,
    file,
    opts = {},
) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    if (!file) {
        throw new Error('Dosya gerekli');
    }

    const formData = new FormData();
    formData.append('File', file);

    const url = `${API_BASE_URL}/members/api/Member/me/profile-picture`;
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
        signal: opts.signal,
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Profil fotoğrafı yanıtı okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Profil fotoğrafı yüklenemedi (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Profil fotoğrafı yüklenemedi';
        throw new Error(msg);
    }

    const rawPath = /** @type {{ data?: unknown }} */ (obj).data;
    const profilePictureUrl = normalizeUploadedProfilePicturePath(rawPath);
    if (!profilePictureUrl) {
        throw new Error('Profil fotoğrafı yanıtı geçersiz');
    }

    const message =
        typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string' && /** @type {{ message: string }} */ (obj).message.trim()
            ? /** @type {{ message: string }} */ (obj).message.trim()
            : 'Profil fotoğrafı güncellendi.';

    return { message, profilePictureUrl };
}

/**
 * Oturum açmış üyeye çocuk ekler.
 * `POST ${API_BASE_URL}/members/api/Member/me/children`
 * @param {string} accessToken
 * @param {{ firstName: string; lastName: string; birthDate: string }} payload
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<{ message: string; child: ReturnType<typeof mapApiChildToView> }>}
 */
export async function addCurrentMemberChild(accessToken, payload, opts = {}) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }

    const firstName = String(payload.firstName || '').trim();
    const lastName = String(payload.lastName || '').trim();
    const birthDate = String(payload.birthDate || '').trim();

    if (!firstName || !lastName || !birthDate) {
        throw new Error('Ad, soyad ve doğum tarihi gerekli');
    }

    const url = `${API_BASE_URL}/members/api/Member/me/children`;
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ firstName, lastName, birthDate }),
        signal: opts.signal,
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Çocuk kaydı yanıtı okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Çocuk eklenemedi (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Çocuk eklenemedi';
        throw new Error(msg);
    }

    const data = /** @type {{ data?: unknown }} */ (obj).data;
    if (!data || typeof data !== 'object') {
        throw new Error('Çocuk kaydı yanıtı geçersiz');
    }

    const message =
        typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string' && /** @type {{ message: string }} */ (obj).message.trim()
            ? /** @type {{ message: string }} */ (obj).message.trim()
            : 'Çocuk başarıyla eklendi.';

    return {
        message,
        child: mapApiChildToView(data),
    };
}

/**
 * Oturum açmış üyenin çocuk kaydını siler.
 * `DELETE ${API_BASE_URL}/members/api/Member/me/children/{childId}`
 * @param {string} accessToken
 * @param {string} childId
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<{ message: string }>}
 */
export async function deleteCurrentMemberChild(
    accessToken,
    childId,
    opts = {},
) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }

    const rawId = String(childId || '').trim();
    if (!rawId) {
        throw new Error('Çocuk kimliği gerekli');
    }

    const url = `${API_BASE_URL}/members/api/Member/me/children/${encodeURIComponent(rawId)}`;
    const res = await fetch(url, {
        method: 'DELETE',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        signal: opts.signal,
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Çocuk silme yanıtı okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Çocuk silinemedi (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Çocuk silinemedi';
        throw new Error(msg);
    }

    const message =
        typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string' && /** @type {{ message: string }} */ (obj).message.trim()
            ? /** @type {{ message: string }} */ (obj).message.trim()
            : 'Çocuk kaydı başarıyla silindi.';

    return { message };
}

/**
 * Oturum açmış üyenin çocuk kaydını günceller.
 * `PUT ${API_BASE_URL}/members/api/Member/me/children`
 * @param {string} accessToken
 * @param {{ id: string; firstName: string; lastName: string; birthDate: string }} payload
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<{ message: string; child: ReturnType<typeof mapApiChildToView> }>}
 */
export async function updateCurrentMemberChild(
    accessToken,
    payload,
    opts = {},
) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }

    const id = String(payload.id || '').trim();
    const firstName = String(payload.firstName || '').trim();
    const lastName = String(payload.lastName || '').trim();
    const birthDate = String(payload.birthDate || '').trim();

    if (!id || !firstName || !lastName || !birthDate) {
        throw new Error('Kimlik, ad, soyad ve doğum tarihi gerekli');
    }

    const url = `${API_BASE_URL}/members/api/Member/me/children`;
    const res = await fetch(url, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ id, firstName, lastName, birthDate }),
        signal: opts.signal,
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Çocuk güncelleme yanıtı okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Çocuk güncellenemedi (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Çocuk güncellenemedi';
        throw new Error(msg);
    }

    const data = /** @type {{ data?: unknown }} */ (obj).data;
    if (!data || typeof data !== 'object') {
        throw new Error('Çocuk güncelleme yanıtı geçersiz');
    }

    const message =
        typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string' && /** @type {{ message: string }} */ (obj).message.trim()
            ? /** @type {{ message: string }} */ (obj).message.trim()
            : 'Çocuk kaydı başarıyla güncellendi.';

    return {
        message,
        child: mapApiChildToView(data),
    };
}

/**
 * Sub üye (eş) bilgilerini günceller.
 * `PUT ${API_BASE_URL}/members/api/Member/submembers/{subMemberId}`
 * @param {string} accessToken
 * @param {string} subMemberId
 * @param {Record<string, string>} payload
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<{ message: string; spouses: ReturnType<typeof mapApiSpouseToView>[] }>}
 */
export async function updateSubMember(
    accessToken,
    subMemberId,
    payload,
    opts = {},
) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }

    const rawId = String(subMemberId || '').trim();
    if (!rawId) {
        throw new Error('Eş kimliği gerekli');
    }

    const url = `${API_BASE_URL}/members/api/Member/submembers/${encodeURIComponent(rawId)}`;
    const res = await fetch(url, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
        signal: opts.signal,
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Eş güncelleme yanıtı okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Eş güncellenemedi (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Eş güncellenemedi';
        throw new Error(msg);
    }

    const data = /** @type {{ data?: unknown }} */ (obj).data;
    const root =
        data && typeof data === 'object'
            ? /** @type {Record<string, unknown>} */ (data)
            : null;
    const rootSubs = root && Array.isArray(root.subMembers)
        ? root.subMembers
        : [];
    const spouses = rootSubs
        .filter((s) => s && typeof s === 'object')
        .map((row) => mapApiSpouseToView(row));

    const message =
        typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string' && /** @type {{ message: string }} */ (obj).message.trim()
            ? /** @type {{ message: string }} */ (obj).message.trim()
            : 'Eş bilgileri güncellendi.';

    return { message, spouses };
}

/**
 * Oturum açmış üyenin profil bilgilerini günceller.
 * `PUT ${API_BASE_URL}/members/api/Member/me`
 * @param {string} accessToken
 * @param {Record<string, string>} payload
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<{ message: string; member: ReturnType<typeof buildMemberDetailViewFromApiData> }>}
 */
export async function updateCurrentMember(accessToken, payload, opts = {}) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }

    const url = `${API_BASE_URL}/members/api/Member/me`;
    const res = await fetch(url, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
        signal: opts.signal,
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Profil güncelleme yanıtı okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Profil güncellenemedi (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Profil güncellenemedi';
        throw new Error(msg);
    }

    const data = /** @type {{ data?: unknown }} */ (obj).data;
    if (!data || typeof data !== 'object') {
        throw new Error('Profil güncelleme yanıtı geçersiz');
    }

    const message =
        typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string' && /** @type {{ message: string }} */ (obj).message.trim()
            ? /** @type {{ message: string }} */ (obj).message.trim()
            : 'Profil bilgileri güncellendi.';

    return {
        message,
        member: buildMemberDetailViewFromApiData(
            /** @type {Record<string, unknown>} */ (data),
        ),
    };
}
